import React, { useEffect, useState } from "react";
import { Box, Paper, Typography, TextField, MenuItem, Button, Alert, Grid } from "@mui/material";
import LanguageIcon from "@mui/icons-material/Language";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import KeyboardIcon from "@mui/icons-material/Keyboard";
import axios from "axios";
import { useLocalSettings } from '../contexts/LocalSettingsContext';
import { useTranslation } from 'react-i18next';
import { backendUrl, API_KEYS } from '../config';

const locales = [
  { code: "fr-FR", label: "Français (France)" },
  { code: "en-US", label: "English (US)" },
  { code: "en-GB", label: "English (UK)" },
  { code: "es-ES", label: "Español (España)" },
];
const keyboards = [
  { code: "fr", label: "Français (AZERTY)" },
  { code: "us", label: "English (QWERTY)" },
  { code: "uk", label: "UK (QWERTY)" },
];

const API_URL = backendUrl;

export default function LocalSettingsPage() {
  const { t } = useTranslation();
  const { settings } = useLocalSettings();
  const [groups, setGroups] = useState([]);
  const [groupId, setGroupId] = useState("");
  const [locale, setLocale] = useState("fr-FR");
  const [keyboard, setKeyboard] = useState("fr");
  const [dateFormat, setDateFormat] = useState("dd/MM/yyyy");
  const [timeFormat, setTimeFormat] = useState("24h");
  const [numberFormat, setNumberFormat] = useState("1 234,56");
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (settings.displayLanguage) setLocale(settings.displayLanguage);
    if (settings.keyboard) setKeyboard(settings.keyboard);
    if (settings.dateFormat) setDateFormat(settings.dateFormat);
    if (settings.timeFormat) setTimeFormat(settings.timeFormat);
    if (settings.numberFormat) setNumberFormat(settings.numberFormat);
  }, [settings]);

  useEffect(() => {
    async function fetchGroups() {
      try {
        const response = await axios.get(`${API_URL}/local-groups/`, {
          headers: {
            'x-api-key': API_KEYS.key,
            'x-api-secret': API_KEYS.secret,
            'accept': 'application/json',
          },
        });
        setGroups(response.data);
      } catch (e) {
        setError(t('errors.loadGroups'));
      }
    }
    fetchGroups();
  }, []);

  useEffect(() => {
    if (!groupId) return;

    async function fetchGroupSettings() {
      try {
        const response = await axios.get(`${API_URL}/api/config/group-configs/${groupId}`, {
          headers: {
            'x-api-key': API_KEYS.key,
            'x-api-secret': API_KEYS.secret,
            'accept': 'application/json',
          },
        });
        const configs = response.data;
        configs.forEach(cfg => {
          if (cfg.key.endsWith("displayLanguage")) setLocale(cfg.value);
          if (cfg.key.endsWith("keyboard")) setKeyboard(cfg.value);
          if (cfg.key.endsWith("dateFormat")) setDateFormat(cfg.value);
          if (cfg.key.endsWith("timeFormat")) setTimeFormat(cfg.value);
          if (cfg.key.endsWith("numberFormat")) setNumberFormat(cfg.value);
        });
        setError("");
      } catch (e) {
        setError(t('errors.loadGroupSettings'));
      }
    }

    fetchGroupSettings();
  }, [groupId]);

  const handleSave = async () => {
    setError("");
    setMessage("");
    if (!groupId) {
      setError(t('errors.noGroupSelected'));
      return;
    }
    setSaving(true);
    try {
      const axiosConfig = {
        headers: {
          'x-api-key': API_KEYS.key,
          'x-api-secret': API_KEYS.secret,
          'accept': 'application/json'
        }
      };
      await axios.post(`${API_URL}/api/config/group-configs/${groupId}`, { key: "displayLanguage", value: locale }, axiosConfig);
      await axios.post(`${API_URL}/api/config/group-configs/${groupId}`, { key: "keyboard", value: keyboard }, axiosConfig);
      await axios.post(`${API_URL}/api/config/group-configs/${groupId}`, { key: "dateFormat", value: dateFormat }, axiosConfig);
      await axios.post(`${API_URL}/api/config/group-configs/${groupId}`, { key: "timeFormat", value: timeFormat }, axiosConfig);
      await axios.post(`${API_URL}/api/config/group-configs/${groupId}`, { key: "numberFormat", value: numberFormat }, axiosConfig);
      setMessage(t('success.groupSettingsSaved'));
    } catch (e) {
      setError(t('errors.saveGroupSettings'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ p: 4, maxHeight: '85vh', overflow: 'auto' }}>
      <Paper elevation={2} sx={{ p: 3, maxWidth: 700, margin: "auto" }}>
        <Typography variant="h5" mb={2}><LanguageIcon sx={{ mr: 1, verticalAlign: "middle" }} /> {t('local-settings.title')}</Typography>
        <TextField
          select
          label={t('local-settings.group')}
          value={groupId}
          onChange={e => setGroupId(e.target.value)}
          fullWidth
          margin="normal"
        >
          <MenuItem value="">{t('local-settings.group.select')}</MenuItem>
          {groups.map(g => (
            <MenuItem key={g.id} value={g.id}>{g.name}</MenuItem>
          ))}
        </TextField>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              select
              label={t('local-settings.displayLanguage')}
              value={locale}
              onChange={e => setLocale(e.target.value)}
              fullWidth
              margin="normal"
            >
              {locales.map(opt => (
                <MenuItem key={opt.code} value={opt.code}>{opt.label}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              select
              label={t('local-settings.keyboard')}
              value={keyboard}
              onChange={e => setKeyboard(e.target.value)}
              fullWidth
              margin="normal"
            >
              {keyboards.map(opt => (
                <MenuItem key={opt.code} value={opt.code}>{opt.label}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label={t('local-settings.dateFormat')}
              value={dateFormat}
              onChange={e => setDateFormat(e.target.value)}
              fullWidth
              margin="normal"
              helperText={t('local-settings.dateFormat.help')}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label={t('local-settings.timeFormat')}
              value={timeFormat}
              onChange={e => setTimeFormat(e.target.value)}
              fullWidth
              margin="normal"
              helperText={t('local-settings.timeFormat.help')}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label={t('local-settings.numberFormat')}
              value={numberFormat}
              onChange={e => setNumberFormat(e.target.value)}
              fullWidth
              margin="normal"
              helperText={t('local-settings.numberFormat.help')}
            />
          </Grid>
        </Grid>
        <Button variant="contained" color="primary" onClick={handleSave} sx={{ mt: 2 }} disabled={saving}>
          {saving ? t('saving') : t('save')}
        </Button>
        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
        {message && <Alert severity="success" sx={{ mt: 2 }}>{message}</Alert>}
      </Paper>
    </Box>
  );
}
